'use client';

import { Check, ChevronDown, ChevronRight, Minus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from '../../molecules/popover/popover';

export interface ScopeConfigItem {
	/** Id univoco della configurazione. */
	id: string;
	/** Nome della configurazione. */
	nome: string;
	/** Numero di soci della configurazione. */
	soci: number;
}

export interface ScopeCerItem {
	/** Id univoco della CER. */
	id: string;
	/** Nome della CER. */
	nome: string;
	/** Configurazioni appartenenti alla CER. */
	configs: ScopeConfigItem[];
}

type CheckState = 'all' | 'partial' | 'none';

export interface ScopeSelectorProps {
	/** Struttura ad albero CER → configurazioni tra cui scegliere. */
	tree: ScopeCerItem[];
	/** Id delle configurazioni attualmente selezionate. */
	selectedIds: string[];
	/** Chiamata alla conferma della selezione (click su "Applica"), con gli id scelti. */
	onApply: (ids: string[]) => void;
	/**
	 * Chiamata quando il puntatore (o il focus) entra su una riga di
	 * configurazione, con il suo id. Il cablaggio la usa per pre-caricare i dati
	 * della configurazione (config + soci) prima del click — ADR-006. Deve essere
	 * idempotente: parte a ogni hover.
	 */
	onPeekConfig?: (id: string) => void;
}

function TriBox({ state }: { state: CheckState }) {
	return (
		<span
			className={cn(
				'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors',
				state === 'none'
					? 'border-border bg-transparent'
					: 'border-accent bg-accent text-[var(--color-bg)]',
			)}
		>
			{state === 'all' && <Check className="h-3 w-3" strokeWidth={3} />}
			{state === 'partial' && <Minus className="h-3 w-3" strokeWidth={3} />}
		</span>
	);
}

/**
 * Selettore multi-ambito nell'header dell'app (accanto alle notifiche): un popover a due
 * colonne per scegliere una o più configurazioni CACER, con selezione a livello di intera
 * CER (checkbox a tre stati) o di singola configurazione. La selezione viene applicata solo
 * al click su "Applica".
 */
export function ScopeSelector({ tree, selectedIds, onApply, onPeekConfig }: ScopeSelectorProps) {
	const cerForSelection = (ids: string[]) => {
		const set = new Set(ids);
		return tree.find((c) => c.configs.some((cfg) => set.has(cfg.id)))?.id ?? tree[0]?.id ?? null;
	};

	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState<string[]>(selectedIds);
	const [activeCerId, setActiveCerId] = useState<string | null>(() => cerForSelection(selectedIds));

	const allLeafIds = useMemo(() => tree.flatMap((c) => c.configs.map((cfg) => cfg.id)), [tree]);
	const draftSet = useMemo(() => new Set(draft), [draft]);

	// Re-seed local draft + active CER whenever the popover opens.
	function handleOpenChange(next: boolean) {
		if (next) {
			setDraft(selectedIds);
			setActiveCerId(cerForSelection(selectedIds));
		}
		setOpen(next);
	}

	function cerState(cer: ScopeCerItem): CheckState {
		const sel = cer.configs.filter((c) => draftSet.has(c.id)).length;
		if (sel === 0) return 'none';
		if (sel === cer.configs.length) return 'all';
		return 'partial';
	}

	const masterState: CheckState =
		draft.length === 0 ? 'none' : draft.length === allLeafIds.length ? 'all' : 'partial';

	function toggleConfig(id: string) {
		setDraft((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	}

	function toggleCer(cer: ScopeCerItem) {
		const ids = cer.configs.map((c) => c.id);
		const isAll = cerState(cer) === 'all';
		setDraft((prev) => {
			const without = prev.filter((x) => !ids.includes(x));
			return isAll ? without : [...without, ...ids];
		});
	}

	function toggleMaster() {
		setDraft(masterState === 'all' ? [] : allLeafIds);
	}

	const activeCer = tree.find((c) => c.id === activeCerId) ?? tree[0] ?? null;

	const triggerLabel = useMemo(() => {
		if (selectedIds.length === 0) return 'Seleziona ambito';
		if (selectedIds.length === allLeafIds.length && allLeafIds.length > 0) return 'Tutte le CER';
		const selSet = new Set(selectedIds);
		const cers = tree.filter((c) => c.configs.some((cfg) => selSet.has(cfg.id)));
		if (selectedIds.length === 1) {
			const cer = cers[0];
			const cfg = cer?.configs.find((c) => selSet.has(c.id));
			if (cer && cfg) return cer.configs.length === 1 ? cer.nome : `${cer.nome} · ${cfg.nome}`;
		}
		return `${cers.length} CER · ${selectedIds.length} config`;
	}, [selectedIds, tree, allLeafIds]);

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<button type="button" className="cer-badge">
					⚡ {triggerLabel}
					<ChevronDown className="h-3 w-3" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-[460px] overflow-hidden p-0"
				style={{ background: 'var(--color-card)' }}
			>
				<div className="grid grid-cols-[200px_1fr]">
					{/* Left: CER list */}
					<div className="border-r border-border">
						<div className="px-3 pb-1.5 pt-2.5 text-[11px] font-bold uppercase tracking-wider text-text-muted">
							CER
						</div>
						<div className="max-h-[320px] overflow-y-auto pb-2">
							<button
								type="button"
								onClick={toggleMaster}
								className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-semibold transition-colors hover:bg-accent/10"
							>
								<TriBox state={masterState} />
								<span>Tutte le CER</span>
							</button>
							<div className="my-1 border-t border-border/60" />
							{tree.map((cer) => {
								const isActive = activeCer?.id === cer.id;
								return (
									<div
										key={cer.id}
										className={cn(
											'flex items-center gap-2 px-3 py-2 transition-colors',
											isActive ? 'bg-accent/10' : 'hover:bg-accent/5',
										)}
									>
										<button
											type="button"
											onClick={() => toggleCer(cer)}
											aria-label={`Seleziona ${cer.nome}`}
										>
											<TriBox state={cerState(cer)} />
										</button>
										<button
											type="button"
											onClick={() => setActiveCerId(cer.id)}
											className="flex flex-1 items-center justify-between gap-1 text-left text-[13px]"
										>
											<span className={cn('truncate', isActive && 'text-accent')}>{cer.nome}</span>
											<span className="flex items-center gap-1 text-[11px] text-text-muted">
												{cer.configs.length}
												<ChevronRight className="h-3 w-3" />
											</span>
										</button>
									</div>
								);
							})}
						</div>
					</div>

					{/* Right: configs of the active CER */}
					<div>
						<div className="truncate px-3 pb-1.5 pt-2.5 text-[11px] font-bold uppercase tracking-wider text-text-muted">
							{activeCer ? `Configurazioni · ${activeCer.nome}` : 'Configurazioni'}
						</div>
						<div className="max-h-[320px] overflow-y-auto pb-2">
							{activeCer?.configs.map((cfg) => {
								const checked = draftSet.has(cfg.id);
								return (
									<button
										key={cfg.id}
										type="button"
										onClick={() => toggleConfig(cfg.id)}
										onMouseEnter={onPeekConfig ? () => onPeekConfig(cfg.id) : undefined}
										onFocus={onPeekConfig ? () => onPeekConfig(cfg.id) : undefined}
										className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-accent/10"
									>
										<TriBox state={checked ? 'all' : 'none'} />
										<span className="flex-1 truncate">{cfg.nome}</span>
										<span className="text-[11px] text-text-muted">{cfg.soci} soci</span>
									</button>
								);
							})}
							{activeCer && activeCer.configs.length === 0 && (
								<div className="px-3 py-4 text-[12px] text-text-muted">Nessuna configurazione</div>
							)}
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between border-t border-border px-3 py-2">
					<button
						type="button"
						onClick={() => setDraft([])}
						className="text-[12px] text-text-muted transition-colors hover:text-text-primary"
					>
						Azzera
					</button>
					<div className="flex items-center gap-2">
						<span className="text-[12px] text-text-muted">{draft.length} selezionate</span>
						<button
							type="button"
							onClick={() => {
								onApply(draft);
								setOpen(false);
							}}
							className="rounded-[6px] bg-accent px-3 py-1 text-[12px] font-semibold text-[var(--color-bg)] transition-opacity hover:opacity-90"
						>
							Applica
						</button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
